// W-0821 — Helpers da página Transferir Títulos para Cobrança.
// dispatchEmChunks: itera array em chunks (LIMITE_LOTE_BOLETOS), agrega responses, reporta progresso.
// extrairMensagemErro: reusa o helper centralizado em ~/Utils/Helpers (LICOES W-0510 L4).

import { LIMITE_LOTE_BOLETOS } from '~/constant';
import { extrairMensagemErro as extrairMensagemErroGlobal } from '~/Utils/Helpers';

/**
 * Itera `arr` em chunks de `chunkSize`, chama `postFn(chunk)` para cada um e agrega responses.
 * Reporta progresso via `onProgress({ chunkAtual, totalChunks })`.
 * Lança o primeiro erro encontrado (não tenta os chunks seguintes).
 *
 * Convenção: postFn retorna `{ transferidos, ignorados, falhasExternas }`
 * (alinhado com LancamentoLoteResponse do backend — ADR-003).
 */
export const dispatchEmChunks = async (
  arr,
  chunkSize,
  postFn,
  onProgress,
  onChunkResponse
) => {
  const tamanho = Math.max(1, chunkSize || LIMITE_LOTE_BOLETOS);
  const totalChunks = Math.ceil(arr.length / tamanho);
  const agregado = {
    transferidos: [],
    ignorados: [],
    falhasExternas: [],
  };

  for (let i = 0; i < totalChunks; i += 1) {
    const inicio = i * tamanho;
    const chunk = arr.slice(inicio, inicio + tamanho);
    if (onProgress) {
      onProgress({ chunkAtual: i + 1, totalChunks });
    }
    // eslint-disable-next-line no-await-in-loop
    const resp = await postFn(chunk);
    const transferidos = (resp && resp.transferidos) || [];
    const ignorados = (resp && resp.ignorados) || [];
    const falhasExternas = (resp && resp.falhas_externas) || (resp && resp.falhasExternas) || [];
    agregado.transferidos.push(...transferidos);
    agregado.ignorados.push(...ignorados);
    agregado.falhasExternas.push(...falhasExternas);
    if (onChunkResponse) {
      onChunkResponse({ transferidos, ignorados, falhasExternas });
    }
  }

  return agregado;
};

/**
 * Wrapper local para o helper global — facilita import único e padroniza fallback.
 */
export const extrairMensagemErro = (err) =>
  extrairMensagemErroGlobal(err) || 'Erro inesperado. Tente novamente.';

/**
 * Calcula soma de valores agrupada por cobrador atribuído.
 * Usado pelo PainelResumoCobradores.
 */
export const resumirAtribuicoes = (boletos, atribuicoes) => {
  const mapa = new Map();
  boletos.forEach((b) => {
    const cobrador = atribuicoes[b.codigo_boleto];
    if (cobrador == null) return;
    const atual = mapa.get(cobrador) || { codigo: cobrador, quantidade: 0, valor: 0 };
    atual.quantidade += 1;
    atual.valor += Number(b.valor) || 0;
    mapa.set(cobrador, atual);
  });
  return Array.from(mapa.values());
};

/**
 * Aplica a regra "cliente com cobrador atual mantém" antes de propagar uma atribuição em lote.
 * Recebe codigos de boletos da página atual + estado atribuições + boletos disponíveis.
 * Retorna os codigos efetivamente afetados.
 */
export const filtrarBoletosSemCobradorAtual = (codigos, boletos) => {
  const setCodigos = new Set(codigos);
  return boletos
    .filter((b) => setCodigos.has(b.codigo_boleto))
    .filter((b) => b.cobrador_atual_codigo == null);
};

/**
 * Formatador BRL — usa Intl com fallback string.
 */
export const formatarBRL = (valor) => {
  if (valor == null || Number.isNaN(Number(valor))) return 'R$ 0,00';
  try {
    return Number(valor).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  } catch (e) {
    return `R$ ${Number(valor).toFixed(2)}`;
  }
};
